import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { generateClient, GraphQLResult } from 'aws-amplify/api';
import { Amplify } from 'aws-amplify';
import { AMPLIFY_CONFIG } from '../config/amplify.config';
import { Subject } from 'rxjs';

export interface DisponibilidadChangedEvent {
  tipoticketid: string;
  eventoid: string;
  url_slug: string;
  nombre_localidad: string;
  aforo_maximo: number;
  aforo_disponible: number;
  tickets_reservados: number;
  estado: string;
  timestamp: string;
}

@Injectable({
  providedIn: 'root'
})
export class AppSyncService {
  private platformId = inject(PLATFORM_ID);
  private isBrowser: boolean;
  private client: any = null;
  private disponibilidadSubscription: any = null;
  private disponibilidadSubject = new Subject<DisponibilidadChangedEvent>();

  // Observable público para que los componentes puedan suscribirse
  disponibilidadChanged$ = this.disponibilidadSubject.asObservable();

  constructor() {
    this.isBrowser = isPlatformBrowser(this.platformId);
    
    // Configurar Amplify y crear cliente solo en el navegador
    if (this.isBrowser) {
      try {
        Amplify.configure(AMPLIFY_CONFIG, { ssr: false });
        this.client = generateClient();
        console.log('✅ AppSyncService: Amplify configurado y cliente creado');
      } catch (error) {
        console.error('❌ Error configurando Amplify en AppSyncService:', error);
      }
    } else {
      console.log('⏭️ AppSyncService: Omitiendo configuración Amplify (SSR)');
    }
  }

  /**
   * Iniciar suscripción a cambios de disponibilidad de tickets
   * @param url_slug - URL slug del evento
   */
  subscribeToDisponibilidad(url_slug: string): void {
    // Validar que estamos en el navegador y que el cliente existe
    if (!this.isBrowser) {
      console.log('⏭️ Suscripción omitida (SSR)');
      return;
    }

    if (!this.client) {
      console.error('❌ Cliente de AppSync no está disponible');
      return;
    }

    // Si ya hay una suscripción activa, cerrarla primero
    this.unsubscribeFromDisponibilidad();

    const subscriptionQuery = `
      subscription OnDisponibilidadChanged($url_slug: String!) {
        onDisponibilidadChanged(url_slug: $url_slug) {
          tipoticketid
          eventoid
          url_slug
          nombre_localidad
          aforo_maximo
          aforo_disponible
          tickets_reservados
          estado
          timestamp
        }
      }
    `;

    console.log('📡 Iniciando suscripción de disponibilidad para:', url_slug);

    try {
      // Crear la suscripción usando la API de graphql
      const subscriptionResult = this.client.graphql({
        query: subscriptionQuery,
        variables: { url_slug }
      }) as any;

      // Verificar si el resultado tiene el método subscribe
      if (subscriptionResult && typeof subscriptionResult.subscribe === 'function') {
        this.disponibilidadSubscription = subscriptionResult.subscribe({
          next: (result: any) => {
            const data = result?.data?.onDisponibilidadChanged;
            if (data) {
              /* console.log('🔄 Actualización de disponibilidad recibida:', data); */
              this.disponibilidadSubject.next(data);
            }
          },
          error: (error: any) => {
            console.error('❌ Error en suscripción de disponibilidad:', error);
            // No propagar el error para evitar romper la aplicación
            // this.disponibilidadSubject.error(error);
          }
        });
      } else {
        console.warn('⚠️ El resultado de graphql no tiene método subscribe');
      }
    } catch (error) {
      console.error('❌ Error al crear suscripción:', error);
      // No propagar el error para evitar romper la aplicación
      // this.disponibilidadSubject.error(error);
    }
  }

  /**
   * Cerrar suscripción de disponibilidad
   */
  unsubscribeFromDisponibilidad(): void {
    if (this.disponibilidadSubscription) {
      console.log('🔌 Cerrando suscripción de disponibilidad');
      // Llamar a unsubscribe si existe el método
      if (typeof this.disponibilidadSubscription.unsubscribe === 'function') {
        this.disponibilidadSubscription.unsubscribe();
      }
      this.disponibilidadSubscription = null;
    }
  }
}
